import {
  createBrowserRouter,
  useRouteError,
  isRouteErrorResponse,
  Link,
} from "react-router-dom";
import App from "@/App";

// Auth
import { AuthProvider } from "@/components/providers/auth-provider";

// Layouts
import DashboardLayout from "@/app/(dashboard)/layout";
import LicitacionDetailLayout from "@/app/(dashboard)/licitaciones/[id]/layout";

// Pages
import DashboardPage from "@/app/(dashboard)/page";
import LicitacionesPage from "@/app/(dashboard)/licitaciones/page";
import LicitacionNuevaPage from "@/app/(dashboard)/licitaciones/nueva/page";
import LicitacionDetailPage from "@/app/(dashboard)/licitaciones/[id]/page";
import LicitacionEditarPage from "@/app/(dashboard)/licitaciones/[id]/editar/page";
import LicitacionChatPage from "@/app/(dashboard)/licitaciones/[id]/chat/page";
import LicitacionAgentesPage from "@/app/(dashboard)/licitaciones/[id]/agentes/page";
import LicitacionDocumentosPage from "@/app/(dashboard)/licitaciones/[id]/documentos/page";
import LicitacionMarcoLegalPage from "@/app/(dashboard)/licitaciones/[id]/marco-legal/page";
import LicitacionPropuestasPage from "@/app/(dashboard)/licitaciones/[id]/propuestas/page";
import ProposalEditorPage from "@/app/(dashboard)/licitaciones/[id]/propuestas/[proposalId]/page";
import ChatPage from "@/app/(dashboard)/chat/page";
import NotificacionesPage from "@/app/(dashboard)/notificaciones/page";
import AnalyticsPage from "@/app/(dashboard)/analytics/page";
import ConfigLLMPage from "@/app/(dashboard)/configuracion/llm/page";
import ConfigAccesosPage from "@/app/(dashboard)/configuracion/accesos/page";
import ConfigPerfilPage from "@/app/(dashboard)/configuracion/perfil/page";
import ConfigTemplatesPage from "@/app/(dashboard)/configuracion/templates/page";
import ConfigMarcoLegalPage from "@/app/(dashboard)/configuracion/marco-legal/page";
import ConfigEquiposPage from "@/app/(dashboard)/configuracion/equipos/page";
import ConfigPerfilEmpresaPage from "@/app/(dashboard)/configuracion/perfil-empresa/page";
import LoginPage from "@/app/login/page";

function RouteError() {
  const error = useRouteError();
  const is404 = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center space-y-4">
        <h1 className="text-6xl font-bold text-gray-300">
          {is404 ? "404" : "Error"}
        </h1>
        <h2 className="text-xl font-semibold text-gray-800">
          {is404 ? "Página no encontrada" : "Algo salió mal"}
        </h2>
        <p className="text-sm text-gray-500 max-w-md">
          {is404
            ? "La página que buscas no existe o fue movida."
            : "Ha ocurrido un error inesperado."}
        </p>
        <Link
          to="/"
          className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-center space-y-4">
        <h1 className="text-6xl font-bold text-gray-300">404</h1>
        <h2 className="text-xl font-semibold text-gray-800">
          Página no encontrada
        </h2>
        <p className="text-sm text-gray-500 max-w-md">
          La página que buscas no existe o fue movida.
        </p>
        <Link
          to="/"
          className="inline-block mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Volver al inicio
        </Link>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    element: <App />,
    errorElement: <RouteError />,
    children: [
      {
        path: "/login",
        element: <LoginPage />,
      },
      {
        element: (
          <AuthProvider>
            <DashboardLayout />
          </AuthProvider>
        ),
        children: [
          { index: true, element: <DashboardPage /> },
          { path: "licitaciones", element: <LicitacionesPage /> },
          { path: "licitaciones/nueva", element: <LicitacionNuevaPage /> },
          {
            path: "licitaciones/:id",
            element: <LicitacionDetailLayout />,
            children: [
              { index: true, element: <LicitacionDetailPage /> },
              { path: "editar", element: <LicitacionEditarPage /> },
              { path: "chat", element: <LicitacionChatPage /> },
              { path: "agentes", element: <LicitacionAgentesPage /> },
              { path: "documentos", element: <LicitacionDocumentosPage /> },
              { path: "marco-legal", element: <LicitacionMarcoLegalPage /> },
              { path: "propuestas", element: <LicitacionPropuestasPage /> },
              {
                path: "propuestas/:proposalId",
                element: <ProposalEditorPage />,
              },
            ],
          },
          { path: "chat", element: <ChatPage /> },
          { path: "notificaciones", element: <NotificacionesPage /> },
          { path: "analytics", element: <AnalyticsPage /> },
          { path: "configuracion/llm", element: <ConfigLLMPage /> },
          { path: "configuracion/accesos", element: <ConfigAccesosPage /> },
          { path: "configuracion/perfil", element: <ConfigPerfilPage /> },
          {
            path: "configuracion/templates",
            element: <ConfigTemplatesPage />,
          },
          {
            path: "configuracion/marco-legal",
            element: <ConfigMarcoLegalPage />,
          },
          {
            path: "configuracion/equipos",
            element: <ConfigEquiposPage />,
          },
          {
            path: "configuracion/perfil-empresa",
            element: <ConfigPerfilEmpresaPage />,
          },
          {
            path: "*",
            element: <NotFoundPage />,
          },
        ],
      },
    ],
  },
]);
